import { useState, useEffect } from "react";
import mascot from "@/assets/futbux-mascot.png";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { claimParentByCpf } from "@/lib/api/parent-invites.functions";
import { savePendingClaim, readPendingClaim, clearPendingClaim } from "@/lib/pending-claim";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Eye, EyeOff, Link2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  getStaffAccessToken,
  clearStaffAccessToken,
  clearStaffLinkSession,
  isStaffLinkSession,
} from "@/lib/staff-access-link";

function isValidCpf(raw: string): boolean {
  const cpf = (raw || "").replace(/\D/g, "");
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf[i], 10) * (10 - i);
  let d1 = (sum * 10) % 11;
  if (d1 === 10) d1 = 0;
  if (d1 !== parseInt(cpf[9], 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf[i], 10) * (11 - i);
  let d2 = (sum * 10) % 11;
  if (d2 === 10) d2 = 0;
  return d2 === parseInt(cpf[10], 10);
}

function formatCpfMask(v: string) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}


export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Entrar ou criar conta | FutBux" },
      {
        name: "description",
        content: "Acesse ou crie sua conta FutBux para gerenciar centros de treinamento, alunos e responsáveis.",
      },
      { property: "og:title", content: "Entrar ou criar conta | FutBux" },
      {
        property: "og:description",
        content: "Acesse ou crie sua conta FutBux para gerenciar centros de treinamento, alunos e responsáveis.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AuthPage,
});

type Tab = "login" | "signup";
type Role = "training_center" | "parent_student";

function getForcedRole(): Role | null {
  if (typeof window === "undefined") return null;
  const r = new URLSearchParams(window.location.search).get("role");
  return r === "parent_student" || r === "training_center" ? r : null;
}

function getNextPath(): string {
  if (typeof window === "undefined") return "/selecionar-perfil";
  const p = new URLSearchParams(window.location.search).get("next");
  if (p && p.startsWith("/") && !p.startsWith("//")) return p;
  if (getForcedRole() === "parent_student") return "/dashboard";
  return "/selecionar-perfil";
}



// Destino final após o login com Google: depende do papel do perfil.
async function resolveOAuthDestination(next: string) {
  if (next !== "/selecionar-perfil") return next;
  const { data } = await supabase.auth.getUser();
  const uid = data.user?.id;
  if (!uid) return next;
  const { data: prof } = await supabase.from("profiles").select("role").eq("id", uid).maybeSingle();
  const r = prof?.role;
  if (r === "system_admin") return "/sa";
  if (r === "parent_student" || r === "parent" || r === "usuario" || r === "user") return "/dashboard";
  return next;

}

async function signInWithGoogle() {
  try {
    const next = getNextPath();
    const forcedRole = getForcedRole();
    // O retorno do OAuth precisa cair em rota pública (/auth); depois disso
    // a própria tela redireciona para o destino correto já com sessão válida.
    const params = new URLSearchParams({ oauth: "1", next });
    if (forcedRole) params.set("role", forcedRole);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/auth?${params.toString()}`,
    });
    if (result.error) {
      toast.error(result.error.message || "Erro ao entrar com Google");
      return;
    }
    if (result.redirected) return;
    localStorage.removeItem("futgestao:activeStaffId");
    localStorage.removeItem("futgestao:activeStaffName");
    window.location.href = await resolveOAuthDestination(next);
  } catch (err: any) {
    toast.error(err?.message || "Erro ao entrar com Google");
  }
}


function GoogleButton({ label }: { label: string }) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={signInWithGoogle}
      className="w-full border-2 border-red-600 ring-2 ring-red-600/40 text-red-700 font-semibold hover:bg-red-50 hover:text-red-800"
    >
      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
      {label}
    </Button>
  );
}

function OrDivider() {
  return (
    <div className="relative my-4">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t" />
      </div>
      <div className="relative flex justify-center text-xs uppercase">
        <span className="bg-card px-2 text-muted-foreground">ou</span>
      </div>
    </div>
  );
}

function AuthPage() {
  const [tab, setTab] = useState<Tab>("login");
  const isOAuthReturn =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("oauth") === "1";

  // Link geral (login) é independente do link de colaborador: se a sessão deste
  // navegador foi aberta por um link com PIN, ela é encerrada localmente aqui
  // para que o login por e-mail e senha funcione de imediato.
  useEffect(() => {
    if (isOAuthReturn) return; // retorno do Google: preservar a sessão recém-criada
    if (!isStaffLinkSession()) return;
    clearStaffLinkSession();
    localStorage.removeItem("futgestao:activeStaffId");
    localStorage.removeItem("futgestao:activeStaffName");
    void supabase.auth.signOut({ scope: "local" }).catch(() => {});
  }, [isOAuthReturn]);

  // Retorno do login com Google: espera a sessão e leva ao destino certo.
  useEffect(() => {
    if (!isOAuthReturn) return;
    let cancelled = false;
    const go = async () => {
      const dest = await resolveOAuthDestination(getNextPath());
      if (!cancelled) window.location.replace(dest);
    };
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) void go();
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session) void go();
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [isOAuthReturn]);




  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img
            src={mascot}
            alt="Mascote FutBux, jogador de futebol em estilo voxel chutando uma bola"
            width={1024}
            height={1280}
            className="h-40 w-auto mb-2 drop-shadow-xl"
          />
          <h1 className="text-2xl font-bold">FutBux</h1>
          <p className="text-sm text-muted-foreground text-center mt-1 max-w-xs">
            Plataforma completa para gestão de escolinhas de futebol.
          </p>
        </div>

        <div className="bg-card rounded-xl shadow-sm border p-5">
          <div className="grid grid-cols-2 gap-1 bg-muted p-1 rounded-lg mb-5">
            <button
              type="button"
              onClick={() => setTab("login")}
              className={cn(
                "py-2 text-sm font-medium rounded-md transition-colors cursor-pointer",
                tab === "login"
                  ? "bg-background text-foreground shadow-sm"
                  : "bg-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              Entrar
            </button>
            <button
              type="button"
              onClick={() => setTab("signup")}
              className={cn(
                "py-2 text-sm font-medium rounded-md transition-colors cursor-pointer",
                tab === "signup"
                  ? "bg-background text-foreground shadow-sm"
                  : "bg-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              Cadastrar
            </button>
          </div>

          {tab === "login" ? <LoginForm /> : <SignupForm onSuccess={() => setTab("login")} />}
        </div>
      </div>
    </div>
  );
}

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const navigate = useNavigate();
  const claimFn = useServerFn(claimParentByCpf);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: signInData, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;
      clearStaffAccessToken();
      clearStaffLinkSession();
      localStorage.removeItem("futgestao:activeStaffId");
      localStorage.removeItem("futgestao:activeStaffName");
      toast.success("Bem-vindo!");
      // Descobre o papel real para decidir o destino
      let dest = getNextPath();
      const uid = signInData.user?.id;
      if (uid && dest === "/selecionar-perfil") {
        const { data: prof } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", uid)
          .maybeSingle();
        const r = prof?.role;
        if (r === "system_admin") {
          navigate({ to: "/sa" });
          return;
        }
        if (r === "parent_student" || r === "parent" || r === "usuario" || r === "user") {
          dest = "/dashboard";
        }
      }


      // Vínculo pendente informado no cadastro (CPF + data de nascimento)
      const pending = readPendingClaim();
      if (pending) {
        try {
          const res: any = await claimFn({
            data: { cpf: pending.cpf, birthDate: pending.birthDate, parentId: null },
          });
          if (res?.needsChoice) {
            // Deixa o vínculo pendente: o portal exibirá o cartão de escolha
            toast.message("Encontramos sua ficha em mais de um Centro. Escolha qual vincular.");
          } else {
            clearPendingClaim();
            toast.success("Ficha vinculada à sua conta!");
          }
          dest = "/dashboard";
        } catch (claimErr: any) {
          clearPendingClaim();
          toast.error(claimErr?.message || "Não foi possível vincular sua ficha automaticamente.");
        }
      }

      navigate({ to: dest });

    } catch (err: any) {
      const msg = (err?.message || "").toLowerCase();
      if (msg.includes("invalid login credentials") || msg.includes("invalid_credentials")) {
        toast.error(
          "E-mail ou senha incorretos. Se você criou a conta com Google, use \"Entrar com Google\". Para redefinir a senha, clique em \"Esqueci minha senha\".",
        );
      } else {
        toast.error(err.message || "Erro ao entrar");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <GoogleButton label="Entrar com Google" />
      <OrDivider />
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="login-email">E-mail</Label>
          <Input
            id="login-email"
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Senha</Label>
          <div className="relative">
            <Input
              id="password"
              type={show ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            className="text-sm text-emerald-600 hover:underline"
          >
            Esqueci minha senha
          </button>
        </div>
        <Button
          type="submit"
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
          disabled={loading}
        >
          {loading ? "Entrando..." : "Entrar"}
        </Button>
      </form>
      <StaffLinkShortcut />
      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />

    </>
  );
}

// Colaborador que já usou um link de acesso rápido neste aparelho volta ao PIN.
// Revalida ao focar/voltar para a aba: a tela de login pode estar em cache no
// celular enquanto o vínculo com o link mudou (mesmo aparelho, perfis diferentes).
function StaffLinkShortcut() {
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    const sync = () => setToken(getStaffAccessToken());
    sync();
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("pageshow", sync);
    return () => {
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("pageshow", sync);
    };
  }, []);
  if (!token) return null;

  return (
    <a
      href={`/acesso/${token}`}
      className="mt-4 block w-full rounded-md border border-emerald-500 py-2 text-center text-sm font-medium text-emerald-600 hover:bg-emerald-50"
    >
      Entrar com PIN (acesso de colaborador)
    </a>
  );
}

function SignupForm({ onSuccess }: { onSuccess: () => void }) {
  const forcedRole = getForcedRole();
  type Step = "chooseRole" | "chooseClaim" | "form";
  const [step, setStep] = useState<Step>(forcedRole ? "form" : "chooseRole");
  const [role, setRole] = useState<Role>(forcedRole ?? "training_center");
  const [claimOpen, setClaimOpen] = useState(false);
  const [confirmEmailOpen, setConfirmEmailOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [claimCpf, setClaimCpf] = useState("");
  const [claimBirth, setClaimBirth] = useState("");
  const [claimChoices, setClaimChoices] = useState<
    { parentId: string; tcId: string; tcName: string }[] | null
  >(null);
  const navigate = useNavigate();
  const claimFn = useServerFn(claimParentByCpf);


  async function runClaim(parentId?: string): Promise<boolean> {
    try {
      const res: any = await claimFn({
        data: { cpf: claimCpf.replace(/\D/g, ""), birthDate: claimBirth, parentId: parentId ?? null },
      });
      if (res?.needsChoice) {
        setClaimChoices(res.options);
        toast.message("Encontramos fichas em mais de um CT. Escolha qual vincular.");
        return false;
      }
      clearPendingClaim();
      toast.success("Ficha vinculada à sua conta!");
      navigate({ to: "/dashboard" });
      return true;
    } catch (err: any) {
      toast.error(err?.message || "Não foi possível vincular a ficha.");
      return false;
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim().toLowerCase() !== emailConfirm.trim().toLowerCase()) {
      toast.error("Os e-mails não conferem");
      return;
    }
    if (password.length < 6) {
      toast.error("A senha deve ter ao menos 6 caracteres");
      return;
    }
    if (claimOpen) {
      if (!isValidCpf(claimCpf)) {
        toast.error("CPF inválido");
        return;
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(claimBirth)) {
        toast.error("Informe a data de nascimento");
        return;
      }
    }
    setLoading(true);
    try {
      const effectiveRole: Role = role;
      const dest = effectiveRole === "parent_student" ? "/dashboard" : getNextPath();
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth?next=${encodeURIComponent(dest)}`,
          data: { name: name.trim(), role: effectiveRole },
        },
      });
      if (error) throw error;
      if (data.session) {
        await supabase.auth.signOut();
      }
      localStorage.removeItem("futgestao:activeStaffId");
      localStorage.removeItem("futgestao:activeStaffName");
      setPendingEmail(email.trim());
      setConfirmEmailOpen(true);
      if (claimOpen) {
        // Guarda a intenção de vínculo: será executada no primeiro login,
        // após a confirmação do e-mail.
        savePendingClaim({ cpf: claimCpf.replace(/\D/g, ""), birthDate: claimBirth });

        toast.message(
          "Conta criada. Confirme seu e-mail e faça login — o vínculo com a ficha do CT será feito na sequência.",
        );
      }
    } catch (err: any) {
      toast.error(err.message || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  if (step === "chooseRole") {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground text-center mb-2">
          Antes de continuar, nos diga quem você é:
        </p>
        <button
          type="button"
          onClick={() => {
            setRole("parent_student");
            setStep("chooseClaim");
          }}
          className="w-full rounded-lg border-2 border-transparent bg-muted/40 hover:border-emerald-500 hover:bg-emerald-50 p-4 text-left transition"
        >
          <div className="font-semibold">Sou Usuário (Responsável / Aluno)</div>
          <div className="text-xs text-muted-foreground mt-1">
            Acompanhar treinos, pagamentos, convocações e súmulas dos alunos.
          </div>
        </button>
        <button
          type="button"
          onClick={() => {
            setRole("training_center");
            setClaimOpen(false);
            setStep("form");
          }}
          className="w-full rounded-lg border-2 border-transparent bg-muted/40 hover:border-emerald-500 hover:bg-emerald-50 p-4 text-left transition"
        >
          <div className="font-semibold">Sou Centro de Treinamento</div>
          <div className="text-xs text-muted-foreground mt-1">
            Gerenciar alunos, cobranças, eventos, súmulas e reservas de espaço.
          </div>
        </button>
      </div>
    );
  }

  if (step === "chooseClaim") {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground text-center mb-2">
          O aluno já está cadastrado em algum Centro de Treinamento?
        </p>
        <button
          type="button"
          onClick={() => {
            setClaimOpen(true);
            setStep("form");
          }}
          className="w-full rounded-lg border-2 border-transparent bg-muted/40 hover:border-emerald-500 hover:bg-emerald-50 p-4 text-left transition"
        >
          <div className="font-semibold">Sim, já tenho ficha em um CT</div>
          <div className="text-xs text-muted-foreground mt-1">
            Vincule sua conta usando CPF e data de nascimento já cadastrados.
          </div>
        </button>
        <button
          type="button"
          onClick={() => {
            setClaimOpen(false);
            setStep("form");
          }}
          className="w-full rounded-lg border-2 border-transparent bg-muted/40 hover:border-emerald-500 hover:bg-emerald-50 p-4 text-left transition"
        >
          <div className="font-semibold">Ainda não tenho cadastro</div>
          <div className="text-xs text-muted-foreground mt-1">
            Criar uma conta nova de responsável.
          </div>
        </button>
        <button
          type="button"
          onClick={() => setStep("chooseRole")}
          className="w-full text-xs text-muted-foreground hover:text-foreground mt-2"
        >
          ← Voltar
        </button>
      </div>
    );
  }

  return (
    <>
      {!forcedRole && (
        <button
          type="button"
          onClick={() => setStep(role === "parent_student" ? "chooseClaim" : "chooseRole")}
          className="text-xs text-muted-foreground hover:text-foreground mb-3"
        >
          ← Voltar
        </button>
      )}
      <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
        <strong>
          {role === "training_center"
            ? "Cadastro de Centro de Treinamento"
            : claimOpen
              ? "Vincular ficha existente"
              : "Cadastro de Usuário"}
        </strong>
      </div>
      <GoogleButton label="Cadastrar com Google" />
      <OrDivider />
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nome Completo</Label>
          <Input
            id="name"
            placeholder="João Silva"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-email">E-mail</Label>
          <Input
            id="signup-email"
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-email-confirm">Confirme seu E-mail</Label>
          <Input
            id="signup-email-confirm"
            type="email"
            placeholder="seu@email.com"
            value={emailConfirm}
            onChange={(e) => setEmailConfirm(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-password">Senha</Label>
          <div className="relative">
            <Input
              id="signup-password"
              type={show ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {claimOpen && (
          <div className="rounded-md border bg-muted/30 p-3 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
              <Link2 className="h-4 w-4" />
              Vincular ficha do Centro de Treinamento
            </div>
            <p className="text-xs text-muted-foreground">
              Informe o CPF e a data de nascimento cadastrados pelo CT. Sua conta
              será vinculada automaticamente à ficha existente (e aos seus filhos).
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="claim-cpf">CPF do responsável</Label>
                <Input
                  id="claim-cpf"
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  value={formatCpfMask(claimCpf)}
                  onChange={(e) => setClaimCpf(e.target.value.replace(/\D/g, "").slice(0, 11))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="claim-birth">Data de nascimento</Label>
                <Input
                  id="claim-birth"
                  type="date"
                  value={claimBirth}
                  onChange={(e) => setClaimBirth(e.target.value)}
                />
              </div>
            </div>
            {claimChoices && claimChoices.length > 0 && (
              <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-2">
                <p className="text-xs text-emerald-800">
                  Encontramos sua ficha em mais de um CT. Escolha qual vincular:
                </p>
                {claimChoices.map((c) => (
                  <Button
                    key={c.parentId}
                    type="button"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => runClaim(c.parentId)}
                  >
                    {c.tcName}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
          disabled={loading}
        >
          {loading ? "Criando..." : "Criar conta"}
        </Button>
      </form>
      <ConfirmEmailDialog
        open={confirmEmailOpen}
        email={pendingEmail}
        onOk={() => {
          setConfirmEmailOpen(false);
          onSuccess();
        }}
      />
    </>
  );
}

function ConfirmEmailDialog({
  open,
  email,
  onOk,
}: {
  open: boolean;
  email: string;
  onOk: () => void;
}) {
  const [resending, setResending] = useState(false);

  async function resendConfirmation() {
    if (!email) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`,
        },
      });
      if (error) throw error;
      toast.success("Reenviamos o e-mail de confirmação.");
    } catch (err: any) {
      toast.error(err?.message || "Não foi possível reenviar o e-mail.");
    } finally {
      setResending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onOk(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirme seu e-mail</DialogTitle>
          <DialogDescription>
            Enviamos um link de confirmação para <strong>{email}</strong>. Abra
            seu e-mail (verifique também a caixa de spam), clique no link e
            depois volte aqui para fazer login.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={resendConfirmation}
            disabled={resending}
          >
            {resending ? "Reenviando..." : "Reenviar e-mail"}
          </Button>
          <Button
            type="button"
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            onClick={onOk}
          >
            OK, entendi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function ForgotPasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success("Enviamos um link de recuperação para seu e-mail.");
      onOpenChange(false);
      setEmail("");
    } catch (err: any) {
      toast.error(err.message || "Erro ao enviar e-mail");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Esqueci minha senha</DialogTitle>
          <DialogDescription>
            Digite seu e-mail e enviaremos um link para redefinir a senha.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="forgot-email">E-mail</Label>
            <Input
              id="forgot-email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              disabled={loading}
            >
              {loading ? "Enviando..." : "Enviar link"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
